import { Route, Routes, useNavigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Error, Login, Search, Splash } from './pages';
import { withProfiler, ErrorBoundary } from '@sentry/react';
import '@janeapp/burrito-design-system/fonts.css';
import './index.css';
import { env, isDev } from './env';
import { ErrorView, LoadingView, LocationInput } from './components';
import '@janeapp/burrito-design-system/globalTheme';
import { useAnalytics, useFeatureFlags, ViewProvider } from './hooks';
import { hasFeatureFlag } from '@janeapp/feature-flags-client';
import { APIProvider } from '@vis.gl/react-google-maps';

const mapsApiKey = env.VITE_GOOGLE_API_KEY;

function App() {
  const navigate = useNavigate();
  const analyticsInitialized = useAnalytics();
  const { isLoading } = useFeatureFlags();

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <LoadingView size="4xl" loading={true} message={''} />
      </div>
    );
  }

  const authEnabled = hasFeatureFlag('AUTHENTICATION_ENABLED');
  const northVanBetaEnabled = hasFeatureFlag('NORTH_VANCOUVER_BETA');

  // universal LocationInput component to be used across pages
  const autocompleteLocationInput = (
    <LocationInput isDisabled={!northVanBetaEnabled} />
  );

  return (
    <ViewProvider>
      <APIProvider
        apiKey={mapsApiKey}
        language="en"
        region="CA"
        version="weekly"
      >
        <main role="main">
          <Routes>
            {authEnabled ? (
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/"
                  element={
                    <ErrorBoundary
                      fallback={
                        <ErrorView
                          onRetry={() => navigate(0)}
                          message="Error in <Splash />"
                          userMessage="Something went wrong while loading Jane Discovery."
                        />
                      }
                      showDialog={isDev()}
                    >
                      <Splash
                        analyticsInitialized={analyticsInitialized}
                        locationInput={autocompleteLocationInput}
                      />
                    </ErrorBoundary>
                  }
                />
              </Route>
            ) : (
              <Route
                path="/"
                element={
                  <ErrorBoundary
                    fallback={
                      <ErrorView
                        onRetry={() => navigate(0)}
                        message="Error in <Splash />"
                        userMessage="Something went wrong while loading Jane Discovery."
                      />
                    }
                    showDialog={isDev()}
                  >
                    <Splash
                      analyticsInitialized={analyticsInitialized}
                      locationInput={autocompleteLocationInput}
                    />
                  </ErrorBoundary>
                }
              />
            )}

            {authEnabled ? (
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/search"
                  element={<Search locationInput={autocompleteLocationInput} />}
                />
              </Route>
            ) : (
              <Route
                path="/search"
                element={<Search locationInput={autocompleteLocationInput} />}
              />
            )}

            {authEnabled && <Route path="/login" element={<Login />} />}
            <Route path="/error" element={<Error />} />
          </Routes>
        </main>
      </APIProvider>
    </ViewProvider>
  );
}
//Capture performance data in Sentry for the entire app
const AppWithProfiler = withProfiler(App);
export default AppWithProfiler;
AppWithProfiler.displayName = 'AppWithProfiler';
